import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid } from "@mui/material";
import { Model, SurveyModel } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.min.css";
import {
  selectFormTemplateById,
  setFormSubmissions
} from "store/reducers/formTemplates/FormTemplatesSlice";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import {
  postFormSubmission,
  getFormSubmissions,
  FormAnswer
} from "apis/v3/formTemplatesApi";
import Mixpanel from "utils/mixpanel";
import LoadingAnimation from "components/v3/sharedComponents/LoadingAnimation";
import SurveyWrapperStyles from "components/v3/sharedComponents/surveys/SurveyWrapper/SurveyWrapper.styles";
import "components/v3/sharedComponents/forms/CustomPanelRenderer";
import "components/v3/sharedComponents/forms/CustomCheckboxWithButton";

export interface FormData {
  formId: string;
  formAnswers: FormAnswer[];
}

interface FormTemplateWrapperProps {
  formId: string;
  onComplete?: () => void;
  onSubmitting?: () => void;
  deferSubmission?: boolean;
  onCompleteWithData?: (data: FormData) => void;
  templateRootClassName?: string;
  preCheckConfirmationQuestion?: boolean;
}

const FormTemplateWrapper: FC<FormTemplateWrapperProps> = ({
  formId,
  onComplete,
  onSubmitting,
  deferSubmission = false,
  onCompleteWithData,
  templateRootClassName,
  preCheckConfirmationQuestion = false
}) => {
  const classes = SurveyWrapperStyles();
  const [surveyModel, setSurveyModel] = useState<SurveyModel | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();

  const formTemplate = useSelector(selectFormTemplateById(formId));

  useEffect(() => {
    if (!formTemplate?.content) {
      return;
    }
    const surveyJson = JSON.parse(formTemplate.content);
    const model = new Model(surveyJson);
    model.showCompletedPage = false;
    model.showNavigationButtons = "none";

    if (preCheckConfirmationQuestion) {
      const qName = `${formId}-confirmation`;
      const q = model.getQuestionByName(qName);
      if (q?.getType() === "checkbox-with-button") {
        const choice = (q as { choices?: { value: string }[] }).choices?.[0];
        if (choice?.value !== undefined) {
          model.setValue(qName, [choice.value]);
        }
      }
    }

    setSurveyModel(model);
  }, [formTemplate, formId, preCheckConfirmationQuestion]);

  const handleComplete = (survey: SurveyModel) => {
    const formAnswers: FormAnswer[] = Object.entries(survey.data).map(
      (answer) => {
        const questionId = answer[0];
        const value = answer[1] as string | boolean | string[];
        const question = survey.getQuestionByName(questionId);

        const displayValue =
          question.getDisplayValue(false) === "Other (describe)"
            ? "Other"
            : question.getDisplayValue(false);

        const textArray = Array.isArray(value)
          ? displayValue.split(",").map((t: string) => t.trim())
          : displayValue;

        const flattenSingleItem = (val: any) =>
          Array.isArray(val) && val.length === 1 ? val[0] : val;

        const finalValue = flattenSingleItem(value);
        const text = flattenSingleItem(textArray);

        const questionAsJson = question.toJSON();
        const formattedQuestion = {
          ...questionAsJson,
          type: question.getType(),
          isRequired: question.isRequired,
          isVisible: question.isVisible
        };

        return {
          questionId,
          value: JSON.stringify(finalValue),
          text: JSON.stringify(text),
          questionAsAsked: JSON.stringify(formattedQuestion)
        };
      }
    );

    if (deferSubmission && onCompleteWithData) {
      onCompleteWithData({ formId, formAnswers });
      return;
    }

    if (onSubmitting) {
      onSubmitting();
    }

    setSubmitting(true);

    const formVersion = formTemplate.version;

    Mixpanel.track(`Form ${formId} submitted, version: ${formVersion}`);

    postFormSubmission({
      formId,
      formAnswers
    })
      .then(() => {
        return getFormSubmissions();
      })
      .then((response: any) => {
        dispatch(setFormSubmissions(response.data));
        setSubmitting(false);

        Mixpanel.track(
          `Form ${formId} submission successful, version: ${formVersion}`
        );

        if (onComplete) {
          onComplete();
        }
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
        setSubmitting(false);

        Mixpanel.track(
          `Form ${formId} submission failed, version: ${formVersion} `,
          {
            error
          }
        );

        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "Error submitting form",
            subText:
              "Sorry, something went wrong when submitting your form. Please try again."
          })
        );
      });
  };

  if (!formTemplate) {
    return (
      <Grid
        container
        className={classes.loadingContainer}
        sx={{ position: "relative" }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)"
          }}
        >
          <LoadingAnimation />
        </Box>
      </Grid>
    );
  }

  if (!surveyModel || submitting) {
    return (
      <Grid
        container
        className={classes.loadingContainer}
        sx={{ position: "relative" }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)"
          }}
        >
          <LoadingAnimation />
        </Box>
      </Grid>
    );
  }

  const compactSurveyLayout = Boolean(templateRootClassName);

  return (
    <Grid
      container
      className={`${classes.surveyContainer} ${classes.formTemplateContainer}${
        templateRootClassName ? ` ${templateRootClassName}` : ""
      }`}
      sx={
        compactSurveyLayout
          ? {
              display: "flex",
              flex: "1 1 0%",
              flexDirection: "column",
              height: 0,
              minHeight: 0,
              overflow: "hidden",
              width: "100%"
            }
          : undefined
      }
    >
      <Box
        sx={{
          width: "100%",
          ...(compactSurveyLayout
            ? {
                display: "flex",
                flex: "1 1 0%",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden"
              }
            : {})
        }}
      >
        <Box
          sx={{
            position: "relative",
            ...(compactSurveyLayout
              ? {
                  display: "flex",
                  flex: "1 1 0%",
                  flexDirection: "column",
                  minHeight: 0,
                  overflow: "hidden"
                }
              : {})
          }}
        >
          <Survey model={surveyModel} onComplete={handleComplete} />
        </Box>
      </Box>
    </Grid>
  );
};

export default FormTemplateWrapper;
